import { FC, memo, useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import { Annotation } from "../../../API";
import HLSStreamPlayer from "../../../pages/data-hub/components/liveview/components/cameraView/HLSStreamPlayer";
import PtControllerDialog from "../../../pages/devices/device-details/components/PtControllerDialog";
import {
  IDeviceTableRow,
  IModel,
} from "../../../pages/devices/hooks/useDeviceTableRows";
import { usePublishNode } from "../../../pages/devices/hooks/usePublishNode";
import { useStreamProcesses } from "../../hooks/useStreamProcesses";
import StyledLoadingButton from "../../providers/theme/design-tokens/LoadingButton/StyledLoadingButton";

import S3Image from "../media/S3Image";
import WarningMessage from "../stream/WarningMessage";
import LiveViewExpandedDialog from "./LiveViewExpandedDialog";
import ExtendButton from "./MaximizeButton";

export enum ViewModeEnum {
  LIVE,
  KEYFRAME,
  KEYFRAME_WITH_ANNOTATIONS,
}

export type ViewMode =
  | ViewModeEnum.LIVE
  | ViewModeEnum.KEYFRAME
  | ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS;

interface KeyframeContainerProps {
  device: IDeviceTableRow;
  s3Key: string;
  showPtButton: boolean;
  viewMode: ViewMode;
  fixedHeight?: string;
  annotationsData?: Annotation[];
  defaultViewMode?: ViewMode;
  isKeyframeLoadedDefaultValue?: boolean;
  setCurrentViewMode?: (mode: ViewMode) => void;
}

const LiveViewContainerComponent: FC<KeyframeContainerProps> = ({
  device,
  s3Key,
  showPtButton,
  viewMode,
  fixedHeight,
  isKeyframeLoadedDefaultValue,
  annotationsData,
  defaultViewMode = ViewModeEnum.KEYFRAME,
  setCurrentViewMode,
}): JSX.Element => {
  const runningService =
    device.deviceData?.hasPanTilt &&
    device.models?.find((model: IModel) => model.isRunning);

  const serviceType: string | null = runningService
    ? runningService.serviceName
    : null;

  const { publishNode } = usePublishNode();

  const { startStreaming, stopStreaming } = useStreamProcesses();

  const [expanded, setExpanded] = useState(false);
  const [isKeyframeLoaded, setKeyframeLoaded] = useState(
    !!isKeyframeLoadedDefaultValue
  );

  const [videoLoading, setVideoLoading] = useState(false);

  useEffect(() => {
    return () => {
      memoizedClosePtControllerCallback();
    };
  }, []);

  const showKeyFrameWithAnnotations =
    annotationsData &&
    typeof isKeyframeLoaded === "boolean" &&
    !!setKeyframeLoaded;

  const changeViewMode = (closeLiveView: boolean = false): void => {
    if (setCurrentViewMode) {
      const streamInput = {
        deviceId: device.rowId,
        nodeId: device.node.id,
        hasPanTilt: device.deviceData?.hasPanTilt,
      };

      if (closeLiveView) {
        setVideoLoading(false);

        stopStreaming(streamInput);

        setCurrentViewMode(ViewModeEnum.KEYFRAME);

        return;
      }

      if (viewMode === ViewModeEnum.LIVE) {
        setVideoLoading(false);

        stopStreaming(streamInput);

        setCurrentViewMode(ViewModeEnum.KEYFRAME);

        return;
      }

      if (
        viewMode === ViewModeEnum.KEYFRAME ||
        viewMode === ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS
      ) {
        setVideoLoading(true);

        startStreaming(streamInput);

        setCurrentViewMode(ViewModeEnum.LIVE);

        return;
      }

      stopStreaming(streamInput);

      if (defaultViewMode === ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS) {
        setCurrentViewMode(ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS);

        return;
      }

      setCurrentViewMode(ViewModeEnum.KEYFRAME);

      setVideoLoading(false);
    }
  };

  const changeExpandedMode = (): void => {
    setExpanded((prev): boolean => !prev);
  };

  const memoizedOpenPtControllerCallback = useCallback(() => {
    setExpanded(false);

    if (viewMode !== ViewModeEnum.LIVE) {
      setVideoLoading(true);

      if (serviceType) {
        publishNode({
          message: JSON.stringify({
            TARGET: "MODEL",
            ACTION: "STOP",
            model_name: serviceType,
            instance_name: device.name,
            DATA: {
              serviceType,
            },
          }),
          nodeId: device.node.id,
        });
      }

      startStreaming({
        deviceId: device.rowId,
        nodeId: device.node.id,
        hasPanTilt: device.deviceData?.hasPanTilt,
      });
    }
  }, [viewMode, device]);

  const memoizedClosePtControllerCallback = useCallback(
    (mode?: ViewMode) => {
      if (mode !== ViewModeEnum.LIVE) {
        stopStreaming({
          deviceId: device.rowId,
          nodeId: device.node.id,
        });
      }
    },
    [device]
  );

  // Set the max width of the container based on the view mode and loading state

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1em",
        alignItems: "center",
      }}
    >
      <LiveViewExpandedDialog
        viewMode={viewMode}
        expanded={expanded}
        s3Key={s3Key}
        changeViewMode={changeViewMode}
        changeExpandedMode={changeExpandedMode}
        openPtController={
          showPtButton ? memoizedOpenPtControllerCallback : undefined
        }
      />

      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: fixedHeight ? fixedHeight : "100%",
          height: fixedHeight ? fixedHeight : "100%",
          borderRadius: "8px",
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
          border: (theme): string =>
            `1px solid ${theme.palette.otherOutlineBorder.main}`,
        }}
      >
        <ExtendButton
          title="Maximize"
          changeExpandedMode={changeExpandedMode}
        />

        {viewMode === ViewModeEnum.KEYFRAME && (
          <S3Image
            s3Key={s3Key}
            fixedHeight={fixedHeight}
            deviceStatus={device.isOnline}
          />
        )}

        {viewMode === ViewModeEnum.LIVE && (
          <HLSStreamPlayer
            fixedHeight={fixedHeight}
            videoLoading={videoLoading}
            setVideoLoading={setVideoLoading}
          />
        )}

        {viewMode === ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS &&
          showKeyFrameWithAnnotations && (
            <S3Image
              variant="annotations"
              annotationsData={annotationsData}
              s3Key={s3Key}
              setKeyframeLoaded={setKeyframeLoaded}
              fixedHeight={fixedHeight}
              deviceStatus={device.isOnline}
              keyFrameMessage="Please wait for the keyframe to retrieve"
            />
          )}
      </Box>

      <Grid
        container
        sx={{
          "& button": {
            width: "100%",
          },
        }}
        spacing="1em"
      >
        <Grid item xs={showPtButton ? 6 : 12}>
          <StyledLoadingButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => changeViewMode()}
          >
            {viewMode !== ViewModeEnum.LIVE ? "Activate" : "Stop"} live view
          </StyledLoadingButton>
        </Grid>

        {showPtButton && (
          <>
            <Grid item xs={6}>
              <PtControllerDialog
                device={device}
                showPtButton
                changeViewMode={changeViewMode}
              />
            </Grid>

            <Grid item>
              <WarningMessage showWarningIcon>
                The model will be stopped when you are running pt control
              </WarningMessage>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

const LiveViewContainer = memo(LiveViewContainerComponent);

export default LiveViewContainer;
